import { Container, Typography, Box, Link, Card, CardContent } from '@mui/material';
import Grid from '@mui/material/Grid2';

const darkQuoteSx = {
    backgroundColor: '#1a1612',
    borderRadius: '6px',
    padding: '20px 24px',
    height: '100%',
    boxSizing: 'border-box'
};

function ActivationSteering() {
    return (
        <Container sx={{ padding: '40px 0' }}>
            <Typography variant="h2" gutterBottom sx={{ textAlign: 'left', marginBottom: '16px' }}>
                Activation Steering for Creative Estrangement
            </Typography>
            <Typography variant="h5" sx={{ textAlign: 'left', marginBottom: '16px', color: '#555' }}>
                Generating evocative narrative through controlled disruption of language models
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', marginBottom: '30px', fontStyle: 'italic' }}>
                Olga Vechtomova, Greg French, Xavier Schneider, Olivier Poulin, and Jeff Bos.
                Published as a long paper at the 17th International Conference on Computational Creativity (ICCC 2026).
            </Typography>

            {/* The Idea in Brief */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    The Idea in Brief
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '16px' }}>
                    Ask a language model to write something strange, and it will <em>perform</em> strangeness:
                    it reaches for the established conventions of experimental writing — ellipses, fragments,
                    familiar surrealist images — because its training has taught it what "strange" is supposed
                    to look like. This work takes a different route. Instead of asking the model through a prompt,
                    we reach inside it: during generation, we add a small directional signal (a steering vector)
                    to the model's internal representations, with no retraining and no instructions.
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                    The steering vector pulls the model toward estrangement while the model's own training pulls
                    it back toward coherent, conventional prose. What emerges from that tug-of-war is not a style
                    the model knows — it is the residue of an internal conflict: quietly impossible narratives
                    the model would never produce on its own, and that no prompt can reliably reach.
                </Typography>
            </Box>

            {/* Making the familiar strange */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Making the Familiar Strange
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '20px' }}>
                    The literary theorist Viktor Shklovsky called it <em>ostranenie</em> — estrangement.
                    Art exists to restore how things are <em>perceived</em>, not how they are known:
                    "the stone made stony again." Estrangement is a minimal displacement from the familiar —
                    an unexplained impossibility embedded in otherwise naturalistic observation, sustained and
                    never resolved. It targets perception rather than the content of reality.
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Solaris (Tarkovsky, 1972)
                                </Typography>
                                <Typography variant="body2">
                                    Rain falls inside the cabin, and the father does not react. Nothing announces
                                    the impossibility; the scene simply continues, and the viewer's perception is
                                    left holding the question.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Twin Peaks (Lynch, 1990)
                                </Typography>
                                <Typography variant="body2">
                                    Coffee will not pour from an inverted cup; speech runs in reverse. The everyday
                                    world stays recognizable while one of its rules quietly stops applying.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Why it matters for creativity */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Why It Matters for Creativity
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '16px' }}>
                    In the earliest phase of creative work, artists are open to any stimulus that makes a new
                    piece feel inevitable. Leonardo da Vinci told his students to study stained walls and
                    streaked stones — projective stimuli whose meaning is unresolved. Meaning forms through the
                    effort to resolve them, and that effort is what activates the creative state. Psychology
                    tells the same story: engagement peaks between the predictable and the random
                    (Berlyne's inverted-U), where a stimulus is neither obvious nor noise.
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                    The aim of this research is therefore not polished output <em>pushed at</em> the artist, but
                    indeterminate text that <em>pulls them in</em>: an unresolved text holds attention open — a
                    sustained attempt to resolve the unresolvable — and that heightened attention is where the
                    artist's own ideas surface. Our earlier LSTM-VAE work produced single lines with this quality;
                    the question here is whether the same opening can be sustained across an extended narrative —
                    a textual environment an artist can inhabit — rather than a single evocative line.
                </Typography>
            </Box>

            {/* How it works */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    How It Works: Two Dials Inside the Model
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    1. A direction, not a prompt
                                </Typography>
                                <Typography variant="body2">
                                    A steering vector is computed as the mean difference between the model's
                                    internal representations of matched literary and neutral passages — 60
                                    contrastive pairs across five modes of estrangement (Contemplative, Lynchian,
                                    Lost Highway, Stalker, Solaris). During generation the vector is simply added
                                    to the hidden states of Llama 3.1 8B. No retraining, no instructions.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    2. A wall for single-layer steering
                                </Typography>
                                <Typography variant="body2">
                                    Sweeping all 32 layers locates the sweet spot at layer 13 (~40% depth), where
                                    the model composes meaning while leaving syntax intact. But one dial hits a
                                    wall: at moderate strength the prose merely grows contemplative, and at high
                                    strength estrangement finally erupts — exactly as coherence collapses.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    3. Separate the two transformations
                                </Typography>
                                <Typography variant="body2">
                                    The resolution is two dials at adjacent layers: layer 13 as a register
                                    dial, controlling the literary texture of the prose, and layer 14 as a
                                    reality-stability dial, controlling whether the world the prose describes
                                    holds together — or quietly stops doing so. The second dial is built from
                                    pairs that differ only in whether something impossible happens.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* What emerges */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    What Emerges
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '20px' }}>
                    With both dials engaged, the model reaches a region neither single-layer steering nor any
                    prompt can reach: <strong>coherent estrangement</strong> — fluent, grammatical narrative in
                    which reality quietly stops holding together.
                </Typography>
                <Grid container spacing={3} sx={{ marginBottom: '20px' }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={darkQuoteSx}>
                            <Typography variant="overline" sx={{ color: '#c49a5c', letterSpacing: '0.14em' }}>
                                Dual-layer steering · Lynchian
                            </Typography>
                            <Typography variant="body1" sx={{ fontStyle: 'italic', color: '#f7f1e4', marginTop: '8px', lineHeight: 1.5 }}>
                                "Emily stood on the edge of the cliff, staring at the cliff. She had been staring
                                at the cliff for hours. They said the cliff was not there."
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#d6c8a8', marginTop: '12px' }}>
                                Every sentence is grammatically complete; the impossibility lives entirely in what
                                the sentences claim about the world.
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={darkQuoteSx}>
                            <Typography variant="overline" sx={{ color: '#c49a5c', letterSpacing: '0.14em' }}>
                                Dual-layer steering · Solaris
                            </Typography>
                            <Typography variant="body1" sx={{ fontStyle: 'italic', color: '#f7f1e4', marginTop: '8px', lineHeight: 1.5 }}>
                                "A single figure sat in the chair. I sit here. … My heart is a bullet that shoots
                                with each heartbeat, rips through my chest. I am a cry without a mouth."
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#d6c8a8', marginTop: '12px' }}>
                                The passage shifts from third to first person mid-paragraph. Nothing like this
                                exists in the training pairs — person shifts, temporal self-contradiction, and
                                identity dissolution emerge from the competing pressures during generation.
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                    This is what separates steering from style transfer: the steering strength carries the output
                    past where any training example sits, so the results are not imitations of the pairs — they
                    are qualities the pairs never contained.
                </Typography>
            </Box>

            {/* Steering vs prompting */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Steering vs. Prompting
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '20px' }}>
                    Prompting a model for "fragmented, experimental prose" produces disruption <em>of</em> syntax;
                    steering produces disruption <em>through</em> syntax. The difference is visible at a glance:
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Prompted for fragmentation
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', marginBottom: '12px' }}>
                                    "foggy light… orange and grey, the world around me… blur. I… wait. for…
                                    something. a bus. i think."
                                </Typography>
                                <Typography variant="body2">
                                    Conventional devices — ellipses, fragments, lowercase. The model performs the
                                    established canon of experimental writing: estrangement through formal
                                    dissolution, with every device instantly recognizable.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Dual-layer steered
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', marginBottom: '12px' }}>
                                    "The bus that had been on time, was not. The bus that was coming, was not."
                                </Typography>
                                <Typography variant="body2">
                                    Grammatically complete sentences whose content contradicts itself —
                                    estrangement through semantic contradiction within intact grammar. A different
                                    mode of disruption, not more of the same one.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Evidence */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Converging Evidence
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                    Six human readers, blind to the condition, rated coherence as remaining high across all
                    conditions (4.25–4.92 out of 5) — and independently tied the strangeness of steered text to
                    its <em>content</em> while tying the strangeness of prompted text to its <em>form</em>. An
                    LLM evaluator scoring seven metrics agreed with an independent second evaluator
                    (ρ = 0.94 on register, ρ = 0.87 on indeterminacy), and the entire two-regime pattern
                    replicates on a different architecture (Mistral 7B).
                </Typography>
            </Box>

            {/* Takeaways */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Main Takeaways
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                    The strange moment needs familiar ground
                                </Typography>
                                <Typography variant="body2">
                                    Occasional, surprising disruption within coherent prose is more estranging than
                                    pervasive fragmentation. Constant strangeness is quickly domesticated; the
                                    irruption of the unfamiliar within a familiar frame is what holds perception open.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                    Two dials reach what one cannot
                                </Typography>
                                <Typography variant="body2">
                                    Dual-layer steering reaches coherent estrangement — high estrangement at high
                                    coherence — a region single-layer steering cannot reach at any strength.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                    The results exceed the training data
                                </Typography>
                                <Typography variant="body2">
                                    Person shifts, temporal self-contradiction, and identity dissolution appear in
                                    the output while being absent from every training pair — they emerge from the
                                    conflict between the two dials, not from imitation.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                    Steering bypasses the prior that prompting cannot
                                </Typography>
                                <Typography variant="body2">
                                    A prompt is filtered through the model's learned conventions, which domesticate
                                    disruption before it surfaces. Steering operates on the representations directly,
                                    setting estrangement against the model's pull toward coherence.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Slides */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Presentation Slides
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                    View the presentation slides from ICCC 2026:
                </Typography>
                <Box sx={{
                    width: '100%',
                    height: '600px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    <iframe
                        src="/pdf/activation-steering-slides.pdf"
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Activation Steering for Creative Estrangement Slides"
                    />
                </Box>
            </Box>

            {/* Resources */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Resources
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Link
                            href="/pdf/activation-steering-paper.pdf"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none', height: '100%', boxSizing: 'border-box' }}
                        >
                            <Card sx={{ textAlign: 'center', height: '100%', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Download Paper
                                    </Typography>
                                    <Typography variant="body2">
                                        Full ICCC 2026 long paper (PDF)
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Link
                            href="/pdf/activation-steering-slides.pdf"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none', height: '100%', boxSizing: 'border-box' }}
                        >
                            <Card sx={{ textAlign: 'center', height: '100%', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Download Slides
                                    </Typography>
                                    <Typography variant="body2">
                                        Presentation slides from ICCC 2026
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Link
                            href="https://ovechtom.github.io/supplementary-material/"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none', height: '100%', boxSizing: 'border-box' }}
                        >
                            <Card sx={{ textAlign: 'center', height: '100%', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Supplementary Materials
                                    </Typography>
                                    <Typography variant="body2">
                                        Generated stories, training pairs, prompts, and analysis
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                </Grid>
            </Box>

            {/* Citation */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Citation
                </Typography>
                <Box sx={{
                    backgroundColor: '#f5f5f5',
                    padding: '20px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem'
                }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        @inproceedings{`{`}vechtomova2026activation,<br />
                        &nbsp;&nbsp;title={`{`}Activation Steering for Creative Estrangement: Generating Evocative Narrative Through Controlled Disruption of Language Models{`}`},<br />
                        &nbsp;&nbsp;author={`{`}Vechtomova, Olga and French, Greg and Schneider, Xavier and Poulin, Olivier and Bos, Jeff{`}`},<br />
                        &nbsp;&nbsp;booktitle={`{`}Proceedings of the 17th International Conference on Computational Creativity (ICCC'26){`}`},<br />
                        &nbsp;&nbsp;year={`{`}2026{`}`}<br />
                        {`}`}
                    </Typography>
                </Box>
            </Box>
        </Container>
    );
}

export default ActivationSteering;
